import { z } from 'zod'
import { validateAnalyticsSuppressionRule } from '../utils/analyticsSuppressionRules'
import { effectiveActions, isSafeFieldPath } from '../../Shared/campaignTests/audienceTest'
// Helper schemas
const actionId = z.string().regex(/^act_/)
const campaignId = z.string().regex(/^cpn_/)
const dimensionId = z.string().regex(/^dim_/)
const eventId = z.string().regex(/^gev_/)
const goalId = z.string().regex(/^gol_/)
const widgetId = z.string().regex(/^wdg_/)
const offerFunnelId = z.string().regex(/^ofn_/)
const offerId = z.string().regex(/^ofr_/)
const segmentId = z.string().regex(/^(seg_\w+|\$everyone)$/)

// Page schema
const page = z
  .array(
    z.object({
      domain: z.string().default('*'),
      path: z.string().default('*'),
      operator: z
        .enum(['equals', 'does not equal', 'contains', 'does not contain', 'starts with', 'ends with', 'glob'])
        .optional()
    })
  )
  .default([])

// Find/Replace schema for content modifications
// Supports both plain text and regex patterns (e.g., "/pattern/gi")
const findReplaceItemSchema = z.object({
  find: z.string(), // Plain text OR regex pattern like "/Hello|Hi/gi"
  replace: z.string() // Replacement text (supports Liquid templates)
})

// Modifications schema for element changes
const modificationsSchema = z
  .object({
    text: z.string().optional(), // Full content replacement
    findReplace: z.array(findReplaceItemSchema).optional() // Find/replace pairs (preserves HTML)
  })
  .passthrough()
  .optional() // passthrough allows additional properties for backward compatibility

// Element Targeting schemas (fingerprint-based element identification)
const fingerprintTextSchema = z.object({
  content: z.string(),
  matchType: z.enum(['exact', 'contains', 'startsWith']).default('exact')
})

const fingerprintVisualSchema = z.object({
  fontSize: z
    .object({
      min: z.number(),
      max: z.number()
    })
    .optional(),
  fontWeight: z
    .object({
      min: z.number()
    })
    .optional(),
  // REMOVED: positionY - depends on viewport-dependent document height
  isAboveFold: z.boolean().optional()
})

const fingerprintElementSchema = z.object({
  tagName: z.string(),
  candidateTags: z.array(z.string()).default(['*'])
})

// NEW: DOM structure signals (viewport-independent)
const fingerprintStructureSchema = z.object({
  siblingIndex: z.number().optional(),
  siblingCount: z.number().optional(),
  parentChain: z.string().optional(),
  depth: z.number().optional()
})

// NEW: Semantic hints (IDs, data attributes, ARIA, classes)
const fingerprintSemanticsSchema = z.object({
  id: z.string().nullable().optional(),
  dataAttributes: z.record(z.string()).nullable().optional(),
  ariaRole: z.string().nullable().optional(),
  ariaLabel: z.string().nullable().optional(),
  meaningfulClasses: z.array(z.string()).optional()
})

// NEW: Content signals (word count, char count)
const fingerprintContentSchema = z.object({
  charCount: z.number().optional(),
  wordCount: z.number().optional(),
  textPrefix: z.string().optional()
})

const fingerprintSchema = z.object({
  text: fingerprintTextSchema,
  visual: fingerprintVisualSchema.optional(),
  element: fingerprintElementSchema.optional(),
  structure: fingerprintStructureSchema.optional(), // NEW: DOM structure signals
  semantics: fingerprintSemanticsSchema.optional(), // NEW: Semantic hints
  content: fingerprintContentSchema.optional() // NEW: Content signals
})

const targetingSchema = z.object({
  // 'fingerprint': Smart Match - finds element by text content and visual properties
  // 'selector': CSS Selector - traditional targeting via CSS selector
  mode: z.enum(['fingerprint', 'selector']),
  fingerprint: fingerprintSchema.optional(),
  fallbackSelector: z.string().optional(),
  selector: z.string().optional()
})

// LogicDefinition schema

export type AnswerConditionOperator =
  | 'equals'
  | 'does not equal'
  | 'greater than'
  | 'greater than or equal to'
  | 'less than'
  | 'less than or equal to'
  | 'between'
  | 'before'
  | 'after'
  | 'on'
  | 'is'
  | 'is not'
  | 'is any of'
  | 'contains'
  | 'starts with'
  | 'is empty'
  | 'is not empty'
  | 'has answered'
  | 'has not answered'
  | 'country is'
  | 'country is not'
  | 'country is any of'
  | 'domain is'
  | 'domain is not'
  | 'domain is any of'
  | 'domain ends with'
  | 'is a business email'
  | 'is a personal email'
  | 'region equals'
  | 'city equals'
  | 'postal code starts with'
  | 'first name equals'
  | 'first name contains'
  | 'has last name'
  | 'has no last name'
  | 'in the past'
  | 'in the future'
  | 'within the next days'
  | 'within the last days'

export type AnswerCondition = {
  $source: 'answers'
  questionId: string
  operator: AnswerConditionOperator
  value?: string | number | string[] | [number, number]
}

export type LogicCondition = {
  $type?: string
  $source?: string
  operator?: string
  occurrence?: string
  value?: string | number | boolean | string[] | [number, number] | { value: string | number }[] | null
  customFieldId?: string | null
  tagIds?: string[]
  segmentId?: string | string[]
  $managedBy?: string
  and?: LogicCondition[]
  or?: LogicCondition[]
  not?: LogicCondition
  query?: string
  questionId?: string | string[]
  // Browser-data sources (jsdata, datalayer).
  variable?: string
  event?: string
  key?: string
  within?: 'pageview' | 'session'
}

const logicDefinition: z.ZodType<LogicCondition> = z.object({}).passthrough()

const nodeSgSchema: z.ZodType = z.object({
  _uuid: z.string().optional(),
  type: z.literal('sg'),
  segmentGroupId: dimensionId.nullable().optional(),
  ask: z.boolean(),
  split: z.union([
    z.literal(false),
    z.array(
      z.object({
        segmentId: segmentId.nullable(),
        child: z.lazy(() => funnelDefinitionNodeRun)
      })
    )
  ])
})

export type NodeSg = z.infer<typeof nodeSgSchema>

const nodeRedirectSchema = z.object({
  _uuid: z.string().optional(),
  type: z.literal('redirect'),
  url: z.string(),
  open: z.boolean(),
  sendSubscriberData: z.boolean().default(false)
})

export type NodeRedirect = z.infer<typeof nodeRedirectSchema>

// FunnelDefinitionNode schema
const funnelDefinitionNode: z.ZodType = z.lazy(() =>
  z.union([
    nodeSgSchema,
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('random'),
      split: z.array(
        z.object({
          child: funnelDefinitionNodeRun
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('yesno'),
      condition: logicDefinition,
      note: z.string().optional(),
      yes: funnelDefinitionNodeRun,
      no: funnelDefinitionNodeRun
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('offer'),
      desiredType: z.enum(['offer', 'form']).optional(),
      offerId: offerId.nullable().optional()
    }),
    nodeRedirectSchema,
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('data'),
      actions: z.array(
        z.object({
          type: z.string(),
          id: actionId,
          score: z.string().optional(),
          adjust: z.number().optional(),
          field: z.string().optional(),
          value: z.string().optional(),
          variableName: z.string().optional(),
          variableValue: z.string().optional(),
          expiresType: z.enum(['never', 'duration']).optional(),
          expiresDuration: z.number().optional(),
          expiresUnit: z.enum(['minutes', 'hours', 'days', 'weeks']).optional()
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('message'),
      content: z.string()
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('highscore'),
      mode: z.enum(['scores', 'variables']).optional(),
      inverse: z.boolean().optional(),
      split: z.array(
        z.object({
          score: z.string().nullable(),
          child: funnelDefinitionNodeRun
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('code'),
      name: z.string(),
      code: z.string(),
      selector: z.string().optional()
    }),
    z
      .object({
        _uuid: z.string(),
        type: z.literal('embed'),
        app: z.literal('senja'),
        content: z
          .union([
            z.object({ kind: z.literal('widget'), id: z.string(), name: z.string(), design: z.string() }).strict(),
            z
              .object({ kind: z.literal('wall_of_love'), slug: z.string(), permalink: z.string(), name: z.string() })
              .strict()
          ])
          .nullable(),
        target: z.union([
          z
            .object({
              kind: z.literal('element'),
              selector: z.string(),
              position: z.enum(['inside', 'before', 'after']),
              pages: logicDefinition
            })
            .strict(),
          z.object({ kind: z.literal('page'), pages: logicDefinition }).strict()
        ])
      })
      .strict(),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('exit')
    })
  ])
)

const funnelDefinitionNodeRun = z.array(funnelDefinitionNode)

const funnelDefinitionRootNode = z.object({
  _uuid: z.string().optional(),
  type: z.literal('root'),
  child: funnelDefinitionNodeRun
})

const formSettingsSchema = z.object({
  showLabels: z.boolean()
})

const formFieldSchema = z.object({
  label: z.string().optional(),
  placeholder: z.string().optional(),
  espField: z.string().optional(),
  displayType: z.string(),
  segmentGroupId: z.string().optional(),
  required: z.boolean().optional(),
  questionDisplay: z.enum(['dropdown', 'radio']).optional(),
  width: z.number(),
  options: z.object({}).optional()
})

// TriggeredAction schema
const triggeredAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('link'),
    settings: z.object({
      url: z.string().nullable(),
      open: z.boolean().optional()
    })
  }),
  z.object({
    type: z.literal('tag'),
    settings: z.object({
      partnerName: z.string(),
      tagIds: z.array(z.string()),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('form'),
    settings: z.object({
      partnerName: z.string().nullable(),
      formIds: z.array(z.string()),
      thanksForConvertingText: z.string().optional(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('list'),
    settings: z.object({
      partnerName: z.string(),
      listIds: z.array(z.string()),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('event'),
    settings: z.object({
      partnerName: z.string(),
      eventIds: z.union([z.string(), z.array(z.string())]),
      eventMetadata: z
        .array(
          z.object({
            key: z.string(),
            value: z.string()
          })
        )
        .optional(),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('automation'),
    settings: z.object({
      partnerName: z.string(),
      automationIds: z.union([z.string(), z.array(z.string())]),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('webhook'),
    settings: z.object({
      actionId: z.string(),
      url: z.string(),
      method: z.enum(['POST', 'PUT', 'PATCH']).default('POST'),
      customPayload: z.string().default(''),
      timeoutMs: z.number().default(10000),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('contact'),
    settings: z.object({
      partnerName: z.string(),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  })
])

/** Twin of App\Support\GoalEventKeys: `RM.push(['conversion', key])` converts the goal event with that key. */
export const JAVASCRIPT_GOAL_EVENT = 'JAVASCRIPT'
export const GOAL_EVENT_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/
export const GOAL_VALUE_MAX = 1_000_000_000
export const GOAL_EVENT_KEY_INVALID =
  'Event keys start with a lowercase letter and use only lowercase letters, numbers and underscores, up to 40 characters.'
export const GOAL_EVENT_KEY_DUPLICATE = 'This event key is already used by another goal.'

const goalEventSchema = z
  .object({
    id: eventId,
    type: z.string(),
    key: z.string().regex(GOAL_EVENT_KEY_PATTERN, GOAL_EVENT_KEY_INVALID).optional(),
    selector: z.string().optional(),
    page
  })
  .superRefine((event, ctx) => {
    if ((event.type === JAVASCRIPT_GOAL_EVENT) !== (event.key !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['key'],
        message: event.key === undefined ? 'Add an event key.' : 'Only JavaScript events have an event key.'
      })
    }
  })

/** The first key used by more than one JavaScript goal event in the project, if any. */
export const duplicateGoalEventKey = (goals: readonly { events: readonly { type: string; key?: string }[] }[]) => {
  const seen = new Set<string>()
  for (const goal of goals) {
    for (const event of goal.events) {
      if (event.type !== JAVASCRIPT_GOAL_EVENT || event.key === undefined) {
        continue
      }
      if (seen.has(event.key)) {
        return event.key
      }
      seen.add(event.key)
    }
  }
  return null
}

// Main schemas
const goalSchema = z.object({
  id: goalId,
  name: z.string(),
  status: z.enum(['active', 'paused']).default('active'),
  isVisibleToUser: z.boolean().default(true),
  /** Fixed value per conversion. Descriptive only; it never decides a winner. */
  value: z.number().min(0).max(GOAL_VALUE_MAX).nullable().optional(),
  events: z.array(goalEventSchema).default([])
})

const goalsSchema = z
  .array(goalSchema)
  .superRefine((goals, ctx) => {
    if (duplicateGoalEventKey(goals) !== null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: GOAL_EVENT_KEY_DUPLICATE })
    }
  })
  .default([])

const segmentSchema = z.object({
  id: segmentId,
  name: z.string()
})

const dimensionSchema = z.object({
  id: dimensionId,
  name: z.string(),
  semanticType: z.string().nullable(),
  isMultiWinning: z.boolean().default(false),
  favorited: z.boolean().default(false),
  syncTo: z.string().nullable(),
  syncToCustomField: z.string().nullable(),
  syncMode: z.enum(['field', 'tag']).nullable().optional().default('field'),
  syncMappings: z.record(z.string()).optional(), // Maps segment IDs to tag/list IDs
  segments: z.array(segmentSchema).default([]),
  signals: z
    .array(
      z.object({
        indicates: segmentId,
        weight: z.string(),
        definition: logicDefinition
      })
    )
    .default([])
})

const variantId = z.string().regex(/^var_/)

export interface JsonObject {
  [key: string]: Json
}
export interface JsonArray extends Array<Json> {
  [index: number]: Json
}
export type Json = null | boolean | number | string | JsonArray | JsonObject

const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([z.null(), z.boolean(), z.number().finite(), z.string(), z.array(jsonSchema), z.record(jsonSchema)])
)

const campaignActionSchema = z.object({
  id: actionId,
  $type: z.string(),
  page,
  selector: z.string().optional(),
  targeting: targetingSchema.optional(),
  modifications: modificationsSchema
})

const fieldPathSchema = z.array(z.string()).refine(isSafeFieldPath, {
  message: 'Use a nonempty object-key path without array indices or prototype keys.'
})

const fieldDeltaSchema = z.discriminatedUnion('op', [
  z.object({ path: fieldPathSchema, op: z.literal('set'), value: jsonSchema }).strict(),
  z.object({ path: fieldPathSchema, op: z.literal('remove') }).strict()
])

const actionOverrideSchema = z.union([
  z.object({ actionId, skip: z.literal(true) }).strict(),
  z.object({ actionId, fields: z.array(fieldDeltaSchema).min(1) }).strict()
])

const versionWeightSchema = z.number().int().min(1).max(99)
const baselineVersionSchema = z
  .object({
    id: z.literal('A'),
    name: z.string(),
    weight: versionWeightSchema
  })
  .strict()
const challengerVersionSchema = z
  .object({
    id: z.enum(['B', 'C', 'D']),
    name: z.string(),
    weight: versionWeightSchema,
    overrides: z.array(actionOverrideSchema),
    actions: z.array(campaignActionSchema)
  })
  .strict()
const audienceTestVersionsSchema = z.tuple([baselineVersionSchema]).rest(challengerVersionSchema)
const audienceTestSchema = z
  .discriminatedUnion('state', [
    z
      .object({
        id: z.string().regex(/^abt_[a-zA-Z0-9]{12}$/),
        state: z.literal('setup'),
        versions: audienceTestVersionsSchema
      })
      .strict(),
    z
      .object({
        id: z.string().regex(/^abt_[a-zA-Z0-9]{12}$/),
        state: z.literal('running'),
        run: z.string().regex(/^abr_[a-zA-Z0-9]{16}$/),
        versions: audienceTestVersionsSchema
      })
      .strict()
  ])
  .superRefine((test, ctx) => {
    if (test.versions.length < 2 || test.versions.length > 4) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['versions'], message: 'Use two to four versions.' })
    }
    if (new Set(test.versions.map((version) => version.id)).size !== test.versions.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['versions'], message: 'Version IDs must be unique.' })
    }
    if (test.versions.reduce((sum, version) => sum + version.weight, 0) !== 100) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['versions'], message: 'Version weights must sum to 100.' })
    }
  })

const variantSchema = z
  .object({
    id: variantId,
    name: z.string(),
    rules: z
      .object({
        all: z
          .array(
            z.object({
              dimensionId,
              segment: segmentId
            })
          )
          .min(1)
          .optional(),
        any: z
          .array(
            z.object({
              dimensionId,
              segment: segmentId
            })
          )
          .min(1)
          .optional()
      })
      .refine((rules) => (rules.all !== undefined) !== (rules.any !== undefined), {
        message: 'An audience must target segments using exactly one of all or any.'
      }),
    actions: z.array(campaignActionSchema).default([]),
    test: audienceTestSchema.optional()
  })
  .superRefine((audience, ctx) => {
    if (!audience.test) {
      return
    }
    const baselineIds = new Set(audience.actions.map((action) => action.id))
    const allIds = new Set(baselineIds)
    const issue = (message: string) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['test'],
        message
      })
    if (baselineIds.size !== audience.actions.length) {
      issue('Baseline action IDs must be unique.')
    }
    for (const version of audience.test.versions) {
      if (version.id === 'A') {
        continue
      }
      const overridden = new Set<string>()
      for (const override of version.overrides) {
        if (!baselineIds.has(override.actionId)) {
          issue('Overrides must reference baseline actions.')
        }
        if (overridden.has(override.actionId)) {
          issue('An action may have only one override per version.')
        }
        overridden.add(override.actionId)
      }
      for (const action of version.actions) {
        if (allIds.has(action.id)) {
          issue('Challenger action IDs must be unique across the audience.')
        }
        allIds.add(action.id)
      }
      try {
        effectiveActions(audience, version.id)
      } catch {
        issue('Field deltas must use safe object-only paths.')
      }
    }
  })

const originalDeploymentSchema = z
  .object({
    testId: z.string().regex(/^abt_[a-zA-Z0-9]{12}$/),
    versionId: z.enum(['B', 'C', 'D']),
    name: z.string(),
    actions: z.array(campaignActionSchema),
    // Absent only on deployments saved before End with deploy recorded them.
    deployedAt: z.string().datetime({ offset: true }).optional(),
    proven: z.boolean().optional()
  })
  .strict()
// The Original page node. Control is the unchanged page, so variants only add changes.
const originalSchema = z
  .object({
    test: audienceTestSchema.optional(),
    deployment: originalDeploymentSchema.optional()
  })
  .strict()
  .superRefine((original, ctx) => {
    if (original.test && original.deployment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['test'],
        message: 'Undeploy first so a new test compares against your updated website.'
      })
    }
    original.test?.versions.slice(1).forEach((version, index) => {
      if ('overrides' in version && version.overrides.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['test', 'versions', index + 1, 'overrides'],
          message: 'Control is the original page, so variants add changes instead of overriding them.'
        })
      }
    })
  })

const campaignSchema = z.object({
  id: campaignId,
  name: z.string(),
  is_active: z.boolean().default(true),
  base_url: z.string(),
  recipe_code: z.string().nullable(),
  dimension_id: dimensionId.nullable(),
  goal_ids: z.array(goalId).default([]),
  outcome: z.enum(['any_goal', 'primary_goal']).optional(),
  condition: z.union([z.boolean(), z.object({})]).default(true),
  metadata: z
    .object({
      generated_by: z.string().optional(),
      source_url: z.string().optional(),
      generated_at: z.string().optional()
    })
    .optional(),
  testing: z.object({
    is_enabled: z.boolean().default(true),
    withhold: z
      .number()
      .nullable()
      .catch(10)
      .transform((v) => v ?? 10)
  }),
  variants: z.array(variantSchema).default([]),
  original: originalSchema.optional(),
  // Keep actions as optional for backward compatibility during migration
  actions: z
    .array(
      z.object({
        id: actionId,
        $type: z.string(),
        page,
        selector: z.string().optional(),
        targeting: targetingSchema.optional(),
        variations: z
          .array(
            z.object({
              segment_id: segmentId.optional(),
              name: z.string().optional(),
              rules: z
                .object({
                  all: z
                    .array(
                      z.object({
                        dimensionId,
                        segment: segmentId
                      })
                    )
                    .optional(),
                  any: z
                    .array(
                      z.object({
                        dimensionId,
                        segment: segmentId
                      })
                    )
                    .optional()
                })
                .optional(),
              modifications: modificationsSchema
            })
          )
          .default([])
      })
    )
    .optional()
})

// Define the schema for the options
const optionScoreSchema = z.object({
  score: z.string(),
  amount: z.number()
})

const optionSchema = z.object({
  id: z.string().startsWith('rqo_'),
  segmentId: z.string().startsWith('seg_'),
  label: z.string(),
  conditions: logicDefinition,
  scores: z.array(optionScoreSchema).default([]),
  imageUrl: z.string().optional()
})

// Define the schema for otherOptions
const otherOptionsSchema = z
  .object({
    enabled: z.boolean().default(false),
    otherLabel: z.string().optional().default('Other'),
    inputType: z.string().optional().default('textarea'),
    inputPrompt: z.string().optional(),
    inputPlaceholder: z.string().optional(),
    nextButtonLabel: z.string().optional(),
    syncToCustomField: z.string().nullable().optional()
  })
  .nullable()

export const questionInputTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'name',
  'address',
  'country',
  'url',
  'date',
  'rating',
  'nps'
])

export const questionContactActionSchema = z.object({
  type: z.enum(['contact', 'form', 'list', 'tag']),
  settings: z.object({
    partnerName: z.string(),
    formIds: z.array(z.string()).optional(),
    listIds: z.array(z.string()).optional(),
    tagIds: z.array(z.string()).optional()
  })
})

const partText = z.string().optional()
const addressPartText = { street: partText, city: partText, region: partText, postcode: partText, country: partText }

export const questionInputSettingsSchema = z.object({
  placeholder: z.string().optional(),
  required: z.boolean().optional(),
  validation: z.string().optional(),
  nextButtonLabel: z.string().nullable().optional(),
  skipButtonLabel: z.string().nullable().optional(),
  numberType: z.enum(['plain', 'currency', 'percentage']).optional(),
  currency: z.string().optional(),
  prefix: z.string().optional(),
  suffix: z.string().optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  step: z.number().nullable().optional(),
  display: z.enum(['field', 'slider']).optional(),
  /** Slider starting position; absent means the midpoint. Snapped to step and clamped to min..max. */
  defaultValue: z.number().nullable().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  ratingMax: z.number().int().min(3).max(10).optional(),
  nameFormat: z.enum(['first_last', 'full']).optional(),
  defaultCountry: z.string().optional(),
  /** Name/address visitor-facing part labels; a full name has none. Absent parts use the English defaults. */
  partLabels: z.object({ first: partText, last: partText, ...addressPartText }).optional(),
  /** Name/address part placeholders (the country part's is its empty option). Absent parts use the defaults. */
  partPlaceholders: z.object({ full: partText, first: partText, last: partText, ...addressPartText }).optional(),
  /** Country question: ISO alpha-2 codes listed first, in this order. */
  pinnedCountries: z.array(z.string()).optional(),
  /** Country question: ISO alpha-2 codes never offered. */
  excludedCountries: z.array(z.string()).optional(),
  /** Country question: pre-select the visitor's detected country; absent means on. */
  detectCountry: z.boolean().optional(),
  /** Date question: `past` allows today or earlier, `future` today or later; absent means any date. */
  dateRange: z.enum(['any', 'past', 'future']).optional(),
  /** Date question: with `dateRange` past/future, the furthest allowed day back or ahead of today. */
  dateRangeDays: z.number().int().min(1).max(36500).optional(),
  mobileOnly: z.boolean().optional(),
  suggestTypoFixes: z.boolean().optional(),
  identify: z.boolean().optional(),
  contactAction: questionContactActionSchema.nullable().optional(),
  consent: z
    .object({
      disclosure: z.object({ enabled: z.boolean(), text: z.string() }).optional(),
      checkbox: z.object({ enabled: z.boolean(), label: z.string() }).optional()
    })
    .optional(),
  syncFieldMap: z.record(z.string()).optional()
})

// Define the main question schema
const questionSchema = z.object({
  id: z.string().startsWith('raq_'),
  text: z.string(),
  name: z.string().optional(),
  dimensionId: z.string().startsWith('dim_').nullable().optional(), // Can be null for input questions
  displayType: z.string().default('buttons'),
  isRandomizingOptionsOrder: z.boolean().default(false),
  otherOptions: otherOptionsSchema,
  options: z.array(optionSchema).default([]),
  displayOptions: z.record(z.any()).default({}),
  isQuestion: z.boolean().optional().default(false), // Flag to identify questions regardless of text content
  inputType: questionInputTypeSchema.nullable().optional(),
  inputSettings: questionInputSettingsSchema.nullable().optional(),
  description: z.string().nullable().optional(),
  syncTo: z.string().nullable().optional(),
  syncToField: z.string().nullable().optional(),
  fill: z
    .object({
      dimensionIds: z.array(z.string().startsWith('dim_')),
      minConfidence: z.number(),
      hints: z.record(z.array(z.string())).optional(),
      score: z
        .object({
          name: z.string(),
          rules: z.array(z.object({ id: z.string(), text: z.string(), points: z.number() }))
        })
        .optional()
    })
    .optional()
})

const rightAskSchema = z.object({
  questions: z.array(questionSchema).default([])
})

const nullableWidgetColor = z.string().nullable().optional()
const nullableWidgetPx = z
  .string()
  .regex(/^\d+(?:\.\d+)?px$/)
  .nullable()
  .optional()
const widgetPaletteSchema = z
  .object({
    borderColor: z.string(),
    contrastColor: z.string(),
    backgroundColor: z.string(),
    textOnContrastColor: z.string(),
    textOnBackgroundColor: z.string(),
    answerButtonColor: nullableWidgetColor,
    textOnAnswerButtonColor: nullableWidgetColor,
    formFieldColor: nullableWidgetColor,
    formFieldTextColor: nullableWidgetColor,
    formFieldBorderColor: nullableWidgetColor,
    placeholderColor: nullableWidgetColor,
    linkColor: nullableWidgetColor,
    backdropColor: nullableWidgetColor,
    backgroundGradientFrom: nullableWidgetColor,
    backgroundGradientTo: nullableWidgetColor,
    backdropOpacity: z.number().min(0).max(1).nullable().optional(),
    questionHeadingColor: nullableWidgetColor,
    questionTextColor: nullableWidgetColor,
    offerHeadingColor: nullableWidgetColor,
    offerTextColor: nullableWidgetColor,
    headerTitleColor: nullableWidgetColor,
    headerSubtitleColor: nullableWidgetColor
  })
  .passthrough()

const widgetFontSizeSchema = z
  .object({
    lineHeight: z.number(),
    headlineFontSize: z.string(),
    formFieldFontSize: z.string(),
    descriptionFontSize: z.string(),
    answerButtonFontSize: z.string(),
    submitButtonFontSize: z.string(),
    headingFontWeight: z.number().min(400).max(900).nullable().optional(),
    bodyFontWeight: z.number().min(400).max(900).nullable().optional(),
    buttonFontWeight: z.number().min(400).max(900).nullable().optional(),
    submitButtonFontWeight: z
      .union([z.number().min(400).max(900), z.literal('theme')])
      .nullable()
      .optional(),
    headerTitleFontSize: nullableWidgetPx,
    headerSubtitleFontSize: nullableWidgetPx
  })
  .passthrough()

const widgetStructureSchema = z
  .object({
    formFieldSpacing: z.string(),
    buttonBorderRadius: z.string(),
    submitButtonBorderRadius: nullableWidgetPx,
    answerButtonSpacing: z.string(),
    containerBorderWidth: z.string(),
    containerBorderRadius: z.string(),
    widgetWidth: z.string().nullable().optional(),
    fieldBorderRadius: nullableWidgetPx,
    buttonLetterSpacing: z
      .string()
      .regex(/^-?\d+(?:\.\d+)?em$/)
      .nullable()
      .optional(),
    submitButtonLetterSpacing: z
      .string()
      .regex(/^-?\d+(?:\.\d+)?em$/)
      .nullable()
      .optional(),
    imageHeight: nullableWidgetPx,
    backdropBlur: nullableWidgetPx,
    containerPaddingY: nullableWidgetPx,
    containerPaddingX: nullableWidgetPx,
    titleSpacing: nullableWidgetPx,
    contentSpacing: nullableWidgetPx,
    submitSpacing: nullableWidgetPx,
    headerSpacing: nullableWidgetPx
  })
  .passthrough()

const widgetLookSchema = z
  .object({
    density: z.enum(['compact', 'comfortable', 'spacious', 'custom']).nullable().optional(),
    buttonStyle: z.enum(['solid', 'outline', 'ghost', 'gradient']).nullable().optional(),
    buttonSize: z.enum(['s', 'm', 'l']).nullable().optional(),
    buttonFullWidth: z.boolean().nullable().optional(),
    buttonUppercase: z.boolean().optional(),
    buttonHover: z.enum(['darken', 'lighten', 'lift', 'none']).nullable().optional(),
    submitButtonStyle: z.enum(['solid', 'outline', 'ghost', 'gradient']).nullable().optional(),
    submitButtonSize: z.enum(['s', 'm', 'l']).nullable().optional(),
    submitButtonFullWidth: z
      .union([z.boolean(), z.literal('theme')])
      .nullable()
      .optional(),
    submitButtonUppercase: z.boolean().nullable().optional(),
    submitButtonHover: z.enum(['theme', 'darken', 'lighten', 'lift', 'none']).nullable().optional(),
    fieldStyle: z.enum(['boxed', 'underlined', 'filled']).nullable().optional(),
    labelPosition: z.enum(['above', 'inside', 'hidden']).nullable().optional(),
    borderStyle: z.enum(['solid', 'dashed', 'dotted']).optional(),
    backgroundType: z.enum(['color', 'gradient', 'image']).optional(),
    backgroundGradientAngle: z.number().min(0).max(360).nullable().optional(),
    backgroundImageUrl: z.string().nullable().optional(),
    imagePosition: z.enum(['top', 'left', 'background']).nullable().optional(),
    entrance: z.enum(['none', 'fade', 'slide', 'scale']).nullable().optional(),
    entranceSpeed: z.enum(['slow', 'normal', 'fast']).optional(),
    stepTransition: z.enum(['slideLeft', 'slideUp', 'slideDown', 'crossfade', 'zoom', 'none']).nullable().optional(),
    stepTransitionFade: z.boolean().nullable().optional(),
    closeButtonStyle: z.enum(['icon', 'circle', 'text']).nullable().optional(),
    closeButtonPosition: z.enum(['left', 'right']).nullable().optional(),
    backButtonStyle: z.enum(['link', 'button', 'icon']).nullable().optional(),
    skipButtonStyle: z.enum(['link', 'button']).nullable().optional()
  })
  .passthrough()

const widgetDeviceOverrideSchema = z
  .object({
    fontSize: widgetFontSizeSchema.partial().strict().optional(),
    structure: widgetStructureSchema.partial().strict().optional(),
    look: widgetLookSchema
      .pick({
        density: true,
        buttonSize: true,
        buttonFullWidth: true,
        submitButtonSize: true,
        submitButtonFullWidth: true
      })
      .strict()
      .optional()
  })
  .strict()

export const widgetOptionsSchema = z
  .object({
    theme: z.string(),
    palette: widgetPaletteSchema,
    paletteDark: widgetPaletteSchema.partial().nullable().optional(),
    darkMode: z.enum(['off', 'auto']).optional(),
    font: z.string().nullable().optional(),
    headingFont: z.string().nullable().optional(),
    typeScale: z.enum(['s', 'm', 'l', 'custom']).optional(),
    fontSize: widgetFontSizeSchema,
    customCss: z.string(),
    placement: z.string().nullable(),
    structure: widgetStructureSchema,
    look: widgetLookSchema.optional(),
    overrides: z
      .object({
        phone: widgetDeviceOverrideSchema.optional(),
        tablet: widgetDeviceOverrideSchema.optional()
      })
      .strict()
      .optional(),
    headerInfo: z
      .object({
        isEnabled: z.boolean(),
        style: z.string(),
        imageUrl: z.string().optional(),
        title: z.string().optional(),
        subtitle: z.string().optional()
      })
      .passthrough()
      .optional(),
    progressBar: z.object({ isEnabled: z.boolean(), baseColor: z.string() }).passthrough().optional(),
    displayTargets: z.object({
      phones: z.boolean(),
      desktop: z.boolean(),
      tablets: z.boolean()
    }),
    style: z.object({
      shadowSize: z.string()
    }),
    backButtonLabel: z.string(),
    backButtonEnabled: z.boolean(),
    nextButtonLabel: z.string().optional(),
    skipButtonLabel: z.string().optional(),
    buttonAnimationTrigger: z.string()
  })
  .passthrough()

const widgetSchema = z
  .object({
    id: widgetId,
    name: z.string().min(1, 'Name is required'),
    type: z.string().optional(),
    isEnabled: z.boolean().optional(),
    modifiedAt: z.number().optional(),
    options: widgetOptionsSchema,
    whenToAskNext: z.enum(['startover', 'immediately', 'pageview', 'session', 'none']).optional(),
    visibility: z
      .object({
        trigger: z.string().optional(),
        areas: z.string().optional(),
        placements: z
          .array(
            z
              .object({
                domain: z.string().min(1).toLowerCase().optional(),
                path: z.string(),
                selector: z.string(),
                position: z.enum(['before', 'after', 'inside'])
              })
              .strict()
          )
          .optional(),
        conditions: logicDefinition.nullable().optional(),
        formSubmitSelector: z.string().optional(),
        /** The `datalayer` trigger's GTM event, optionally only pushes whose `key` equals `value` (ENG-1617). */
        dataLayerEvent: z
          .object({ event: z.string(), key: z.string().optional(), value: z.string().optional() })
          .optional(),
        instantDismiss: z.boolean().optional(),
        dismissRule: z
          .object({
            type: z.enum(['session', 'duration', 'forever']),
            duration: z.number().optional(),
            unit: z.enum(['minutes', 'hours', 'days', 'weeks']).optional()
          })
          .optional()
      })
      .passthrough()
      .optional(),
    contentId: z.union([offerFunnelId, offerId]).nullable().optional(),
    /** Hosted page (`pg_…`) that owns this widget. Owned widgets are edited with their page and hidden from widget lists. */
    ownerPageId: z
      .string()
      .regex(/^pg_[A-Za-z0-9]+$/)
      .nullable()
      .optional()
  })
  .superRefine((widget, context) => {
    if (widget.type !== 'inline') {
      return
    }
    for (const device of ['phone', 'tablet'] as const) {
      if (widget.options.overrides?.[device]?.structure?.widgetWidth !== undefined) {
        context.addIssue({
          code: 'custom',
          path: ['options', 'overrides', device, 'structure', 'widgetWidth'],
          message: 'Embedded widget width cannot be overridden per device'
        })
      }
    }
  })

const flowSchema = z.object({
  id: offerFunnelId,
  name: z.string(),
  accessedAt: z.number().optional(),
  modifiedAt: z.number().optional(),
  isSystemFlow: z.boolean().optional(),
  funnelDefinition: funnelDefinitionRootNode
})

const offerSchema = z.object({
  id: offerId,
  name: z.string(),
  isEnabled: z.boolean(),
  parts: z.object({
    title: z.string(),
    description: z.string().nullable(),
    buttonText: z.string().nullable(),
    imageUrl: z.string().optional(),
    imageFocalPoint: z
      .object({
        x: z.number().min(0).max(100),
        y: z.number().min(0).max(100)
      })
      .optional()
  }),
  triggeredAction
})

const rightCtaSchema = z.object({
  settings: z
    .object({
      isEnabled: z.boolean().optional(),
      foregroundColor: z.string().optional(),
      backgroundColor: z.string().optional(),
      buttonForegroundColor: z.string().optional(),
      buttonBackgroundColor: z.string().optional(),
      isBrandingHidden: z.boolean().optional(),
      thanksForAnsweringText: z.string().optional(),
      submittingText: z.string().optional(),
      whyAreWeAskingThisText: z.string().optional(),
      emailFieldLabel: z.string().optional(),
      questionAnswerLabel: z.string().optional(),
      ga: z.boolean().optional(),
      disablePrefillFormFields: z.boolean().optional(),
      enhancedFlowReporting: z.boolean().optional()
    })
    .optional(),
  widgets: z.array(widgetSchema).optional(),
  offerFunnels: z.array(flowSchema).optional(),
  /** Widget flow IDs, highest priority first (ENG-1616). Absent: widgets keep their stored order. */
  flowPriority: z.array(offerFunnelId).optional(),
  offers: z.array(offerSchema).optional()
})

const settingsSchema = z.object({
  clientStorage: z.enum(['cookies', 'localStorage']).optional(),
  crossDomainTracking: z.boolean().optional(),
  jsHooks: z.object({ afterFormSubmit: z.string().optional() }).optional(),
  analyticsSuppressionRules: z
    .array(
      z.string().refine((value) => validateAnalyticsSuppressionRule(value) === null, {
        message: 'Use a hostname, IPv4 address, or IPv4 CIDR. IPv6 is not supported yet.'
      })
    )
    .default([]),
  waitForConsent: z.boolean().optional(),
  integrations: z.array(z.object({ name: z.string(), enabled: z.boolean() })).default([]),
  enhancedFlowReporting: z.boolean().optional(),
  autoRefreshEnabled: z.boolean().optional(),
  emailWatcher: z
    .object({
      enabled: z.boolean().optional()
    })
    .optional()
})

const phraseSchema = z.object({
  id: z.string(),
  fallbackText: z.string(),
  segmentGroupId: z.string().nullable(),
  map: z.record(z.string())
})

const attributeSchema = z.object({
  label: z.string(),
  value: z.string(),
  internal: z.boolean(),
  overwrite: z.boolean(),
  destination: z.string()
})

// Config schema
const configSchema = z.object({
  id: z.number(),
  version: z.literal('2026-10-21'),
  domainNames: z.array(z.string()).default([]),
  onboarding: z.object({}).default({}),
  settings: settingsSchema.default({}),
  goals: goalsSchema,
  dimensions: z.array(dimensionSchema).default([]),
  campaigns: z.array(campaignSchema).default([]),
  rightAsk: rightAskSchema.default({}),
  rightCta: rightCtaSchema.optional(),
  phrases: z.array(phraseSchema).optional(),
  attributes: z.array(attributeSchema).optional(),
  tags: z.array(z.object({ id: z.string() }).passthrough()).optional()
})

// Shared team identifier accepted by dashboard APIs and route state.
export type TeamPid = string | number

export type Config = z.infer<typeof configSchema>
export type Goal = z.infer<typeof goalSchema>
export type GoalEvent = z.infer<typeof goalEventSchema>
export type Dimension = z.infer<typeof dimensionSchema>
export type Segment = z.infer<typeof segmentSchema>
export type LogicDefinition = z.infer<typeof logicDefinition>
export type LogicDefinitionItem = LogicCondition
export type Campaign = z.infer<typeof campaignSchema>
export type Variant = z.infer<typeof variantSchema>
export type CampaignAction = z.infer<typeof campaignActionSchema>
export type FieldDelta = z.infer<typeof fieldDeltaSchema>
export type ActionOverride = z.infer<typeof actionOverrideSchema>
export type BaselineVersion = z.infer<typeof baselineVersionSchema>
export type ChallengerVersion = z.infer<typeof challengerVersionSchema>
export type AudienceTest = z.infer<typeof audienceTestSchema>
export type OriginalNode = z.infer<typeof originalSchema>
export type OriginalDeployment = z.infer<typeof originalDeploymentSchema>
export type VersionId = BaselineVersion['id'] | ChallengerVersion['id']
export type ChallengerId = ChallengerVersion['id']
export type RightAsk = z.infer<typeof rightAskSchema>
export type Question = z.infer<typeof questionSchema>
export type QuestionInputType = z.infer<typeof questionInputTypeSchema>
export type QuestionInputSettings = z.infer<typeof questionInputSettingsSchema>
export type QuestionContactAction = z.infer<typeof questionContactActionSchema>
export type QuestionOption = z.infer<typeof optionSchema>
export type RightCta = z.infer<typeof rightCtaSchema>
export type Widget = z.infer<typeof widgetSchema>
export type Offer = z.infer<typeof offerSchema>
export type Flow = z.infer<typeof flowSchema>
export type FunnelDefinitionNode = z.infer<typeof funnelDefinitionNode>
export type FunnelDefinitionRootNode = z.infer<typeof funnelDefinitionRootNode>
export type TriggeredAction = z.infer<typeof triggeredAction>
export type Settings = z.infer<typeof settingsSchema>
export type Attribute = z.infer<typeof attributeSchema>
export type Phrase = z.infer<typeof phraseSchema>
export type Targeting = z.infer<typeof targetingSchema>
export type Fingerprint = z.infer<typeof fingerprintSchema>
export type FingerprintText = z.infer<typeof fingerprintTextSchema>
export type FingerprintVisual = z.infer<typeof fingerprintVisualSchema>
export type FingerprintElement = z.infer<typeof fingerprintElementSchema>
export type FingerprintStructure = z.infer<typeof fingerprintStructureSchema>
export type FingerprintSemantics = z.infer<typeof fingerprintSemanticsSchema>
export type FingerprintContent = z.infer<typeof fingerprintContentSchema>
export type FindReplaceItem = z.infer<typeof findReplaceItemSchema>
export type Modifications = z.infer<typeof modificationsSchema>
// Export schemas
export {
  configSchema,
  goalSchema,
  goalEventSchema,
  dimensionSchema,
  campaignSchema,
  variantSchema,
  campaignActionSchema,
  fieldDeltaSchema,
  actionOverrideSchema,
  audienceTestSchema,
  rightAskSchema,
  rightCtaSchema,
  flowSchema,
  funnelDefinitionNode,
  funnelDefinitionRootNode,
  triggeredAction,
  questionSchema,
  offerSchema,
  widgetSchema,
  attributeSchema,
  phraseSchema,
  targetingSchema,
  fingerprintSchema,
  findReplaceItemSchema,
  modificationsSchema
}
